import mapHydro from 'assets/pictures/markers/map_hydro.png'
import mapInstallations from 'assets/pictures/markers/map_installations.png'
import mapPiezoGreen from 'assets/pictures/markers/map_piezo_green.png'
import mapPiezoOrange from 'assets/pictures/markers/map_piezo_orange.png'
import mapPiezoRed from 'assets/pictures/markers/map_piezo_red.png'
import mapPiezoWhite from 'assets/pictures/markers/map_piezo_white.png'
import mapPluvio from 'assets/pictures/markers/map_pluvio.png'
import mapQualite from 'assets/pictures/markers/map_qualite.png'
import PropTypes from 'prop-types'
import { compact, groupBy, includes, orderBy, template, uniq, uniqBy } from 'lodash'
import moment from 'moment'
import React from 'react'
import i18n from 'simple-react-i18n'
import ProgressBar from '../../components/progress/ProgressBar'
import { getParametersNature, getParametersType } from '../../referencial/util/ReferencialUtils'
import { getStatusInformation } from '../../referencial/util/StatusUtils'
import { getI18nTitleDataLength, normalizeString, searchAllCharacters } from '../../utils/StringUtil'
import { getDate } from '../../utils/DateUtil'
import { BugReport, Build, DirectionsRun, MoreHoriz, ScienceOutlined } from '@mui/icons-material'
import { Stack, Tooltip } from '@mui/material'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import { favOrange } from '../../utils/constants/ColorTheme'
import { CAMPAIGN_TYPES_CONSTANTS, QUALITY_CAMPAIGN_TYPES_CONSTANTS } from 'campaign/constants/CampaignConstants'

const getPixelSize = (size) => {
    switch (size) {
        case 'tiny': return '1rem'
        case 'small': return '2rem'
        case 'medium': return '4rem'
        case 'large': return '6rem'
        default: return '2rem'
    }
}

const getCampaignIcon = (type, size) => {
    const fontSize = getPixelSize(size)
    if (type === CAMPAIGN_TYPES_CONSTANTS.PREVENTIVE || type == 'G') {
        return <Tooltip title={i18n.preventive}><DirectionsRun sx={{ fontSize }} /></Tooltip>
    }
    if (type === CAMPAIGN_TYPES_CONSTANTS.CURATIVE || type == 'M') {
        return <Tooltip title={i18n.curative}><Build sx={{ fontSize }} /></Tooltip>
    }
    return <Tooltip title={i18n.other}><MoreHoriz sx={{ fontSize }} /></Tooltip>
}

const getQualityCampaignIcon = (type, size) => {
    const fontSize = getPixelSize(size)
    if (type === QUALITY_CAMPAIGN_TYPES_CONSTANTS.HYDROBIO) {
        return <Tooltip title={i18n.hydrobio}><BugReport sx={{ fontSize }} /></Tooltip>
    }
    if (type === QUALITY_CAMPAIGN_TYPES_CONSTANTS.CHEMICAL_PHYSICS) {
        return <Tooltip title={i18n.chemicalPhysics}><ScienceOutlined sx={{ fontSize }} /></Tooltip>
    }
    return <Tooltip title={i18n.other}><MoreHoriz sx={{ fontSize }} /></Tooltip>
}

const getNbStationValidated = (piezometerCampaignStations, idCampaign) => {
    if (piezometerCampaignStations) {
        return piezometerCampaignStations.filter(p => p.campaignId === idCampaign && p.visitValidated === true).length
    }
    return 0
}

const getNbStation = (piezometerCampaignStations, idCampaign) => {
    if (piezometerCampaignStations) {
        return piezometerCampaignStations.filter(p => p.campaignId === idCampaign).length
    }
    return 0
}

const getCampaignProgress = (nbStationValidated, nbStation) => {
    if (nbStation) {
        return (nbStationValidated / nbStation) * 100
    }
    return 0
}

const getContact = (contacts, code) => {
    if (code && contacts) {
        return contacts.find(o => o.code == code)
    }
    return null
}
// eslint-disable-next-line valid-jsdoc
/**
 * @deprecated use TableProgressBar
 */
const getProgressBar = (eventCount, stations, progress, labelSing = i18n.stationOn, labelPlur = i18n.stationsOn) => {
    const tooltip = `${eventCount} ${getI18nTitleDataLength(labelSing, labelPlur, eventCount)} ${stations} (${Math.round(progress)}%)`
    return (
        <div style={{ width: '100px' }}>
            <ProgressBar progress={progress} tooltip={tooltip} />
        </div>
    )
}

const TableProgressBar = ({
    nb,
    nbTotal,
    progress,
    labelSing = i18n.stationOn,
    labelPlur = i18n.stationsOn,
}) => {
    const tooltip = `${nb} ${getI18nTitleDataLength(labelSing, labelPlur, nb)} ${nbTotal} (${Math.round(progress)}%)`
    return (
        <div style={{ width: '100px' }}>
            <ProgressBar progress={progress} tooltip={tooltip} />
        </div>
    )
}

TableProgressBar.propTypes = {
    nb: PropTypes.number,
    nbTotal: PropTypes.number,
    progress: PropTypes.number,
    labelSing: PropTypes.string,
    labelPlur: PropTypes.string,
}

const getCampaignsWithBookmarks = (campaigns, currentCampaigns, bookmarkIds) => [
    ...orderBy(campaigns.filter(c => bookmarkIds.includes(c.id)), c => moment(c.beginningApplication, 'DD/MM/YYYY').valueOf(), 'desc'),
    ...currentCampaigns.filter(c => !bookmarkIds.includes(c.id)),
]

const getCampaignNameCell = (name, isBookmarked) => isBookmarked ? {
    value: (
        <Stack direction='row' alignItems='center' spacing={0.5}>
            <StarRoundedIcon sx={{ color: favOrange, fontSize: '18px' }} />
            <span>{name}</span>
        </Stack>
    ),
    sortValue: name,
} : name

const getContactCode = (contactCode, contacts) => {
    const contact = getContact(contacts, contactCode)
    return contact ? contact.code : null
}

const getContactName = (contactCode, contacts) => {
    const contact = getContact(contacts, contactCode)
    return contact ? contact.name : i18n.unknown
}

const formatStationSelectedPopup = (stations, campaignEvents) => {
    const date = +moment()
    const monitoringByStation = groupBy(campaignEvents.filter(event => event.eventType === 'S' && (!event.closeDate || event.closeDate > date)), 'stationId')
    const toPlanByStation = groupBy(campaignEvents.filter(event => event.eventType === 'P' && (!event.closeDate || event.closeDate > date)), 'stationId')
    return stations.map(({ id, code = '', name = '', townCode, installationType, visitStatus, idVisit }) => ({
        labelSearch: searchAllCharacters(`${code}${name}`),
        townCode,
        id,
        code,
        name,
        monitoring: (monitoringByStation[id] || []).length,
        toPlanEvents: (toPlanByStation[id] || []).length,
        installationType,
        visitStatus,
        idVisit,
    }))
}

const formatParameterSelectedPopup = (parameters, showCode = true) => {
    const types = getParametersType()
    const natures = getParametersNature()
    return parameters.map(({ code, name, active, shortLabelWithCode, shortLabel, typeParam, nature, status, unitReference1 }) => ({
        labelSearch: normalizeString(`${code}${name}`),
        code,
        id: code,
        name: (!showCode && shortLabel) || shortLabelWithCode,
        type: types[typeParam],
        nature: natures[nature],
        status: status ? getStatusInformation(status) : i18n.validated,
        unitCode: unitReference1,
        active: active === '1',
    }))
}

const getMarkerIcon = (stationType, color) => {
    switch (stationType) {
        case 'piezometry': {
            switch (color) {
                case 'green': return mapPiezoGreen
                case 'orange': return mapPiezoOrange
                case 'red': return mapPiezoRed
                default: return mapPiezoWhite
            }
        }
        case 'pluviometry': return mapPluvio
        case 'hydrometry': return mapHydro
        case 'quality': return mapQualite
        case 'installation': return mapInstallations
        default: return undefined
    }
}

const getAnalysisColorFollowing = (analysis, campaignParameters) => {
    const nbAnalysis = uniqBy(analysis, 'parameter').length
    if (nbAnalysis >= campaignParameters.length) {
        return 'green'
    } else if (nbAnalysis < campaignParameters.length && nbAnalysis !== 0) {
        return 'orange'
    } else if (nbAnalysis === 0) {
        return 'red'
    } return 'blue'
}

function *generateNegId() {
    let id = -1
    while (true) {
        yield id--
    }
}
const negIdGenerator = generateNegId()

const removeObjectivesFromPlannings = (plannings, objectiveIds) => plannings.map(planning => ({
    ...planning,
    objectivesPlan: planning.objectivesPlan.filter(id => !includes(objectiveIds, id)),
}))

const removeObjectivesFromExceptions = (exceptions, objectiveIds) => compact(exceptions.map(exception => {
    const cleaned = {
        ...exception,
        objectivesToAddPlan: exception.objectivesToAddPlan.filter(id => !includes(objectiveIds, id)),
        objectivesToDeletePlan: exception.objectivesToDeletePlan.filter(id => !includes(objectiveIds, id)),
    }
    return cleaned.objectivesToAddPlan.length || cleaned.objectivesToDeletePlan.length ? cleaned : undefined
}))

const getStationPlanningObjectiveIds = (planning, planningExceptions, stationId) => {
    const applicableExceptions = planningExceptions.filter(({ stationsPlan }) => includes(stationsPlan, stationId))
    const toDelete = uniq(applicableExceptions.flatMap(({ objectivesToDeletePlan }) => objectivesToDeletePlan))
    const toAdd = uniq(applicableExceptions.flatMap(({ objectivesToAddPlan }) => objectivesToAddPlan))
    return uniq([...(planning.objectivesPlan || []).filter(id => !includes(toDelete, id)), ...toAdd])
}

const getPeriodTitle = (label, fromToTemplate, fromTemplate, startDate, endDate) => {
    if (!startDate) return label
    if (!endDate) return template(fromTemplate)({ startDate })
    return template(fromToTemplate)({ startDate, endDate })
}

const getPlanningPeriodTitle = (startDate, endDate) => getPeriodTitle(i18n.planning, i18n.planningFromXToX, i18n.planningFromX, getDate(startDate), getDate(endDate))

const getCampaignPeriodTitle = (beginningApplication, endingApplication) => getPeriodTitle(i18n.campaign, i18n.campaignFromXToX, i18n.campaignFromX, beginningApplication, endingApplication)

// repli sur producer pour les planifications enregistrées avant la multisélection
const getPlanningProducers = (planning = {}) => (planning.producers?.length ? planning.producers : compact([planning.producer]))

const getPlanningProducersName = (planning, contacts = []) => compact(getPlanningProducers(planning).map(id => contacts.find(c => c.id === id)?.name)).join(', ')

const getCampaignContributorIds = (campaign = {}) => uniq(compact([campaign.contributorCode, campaign.recipientCode, ...(campaign.secondaryLabos || []), campaign.samplerCode].map(id => parseInt(id))))

// unité Sandre "Sans objet" : appliquée aux paramètres sans unité de référence (sinon ignorés par les demandes EDILABO)
const DEFAULT_CAMPAIGN_PARAMETER_UNIT = 'X'

const getDefaultCampaignParameterUnit = parameter => parameter?.unitReference1 || DEFAULT_CAMPAIGN_PARAMETER_UNIT

export {
    DEFAULT_CAMPAIGN_PARAMETER_UNIT,
    getDefaultCampaignParameterUnit,
    getPlanningProducers,
    getPlanningProducersName,
    getCampaignContributorIds,
    getPlanningPeriodTitle,
    getCampaignPeriodTitle,
    getPixelSize,
    getCampaignIcon,
    getQualityCampaignIcon,
    formatStationSelectedPopup,
    formatParameterSelectedPopup,
    getNbStationValidated,
    getContact,
    getNbStation,
    getCampaignProgress,
    getProgressBar,
    TableProgressBar,
    getCampaignsWithBookmarks,
    getCampaignNameCell,
    getContactCode,
    getContactName,
    getMarkerIcon,
    getAnalysisColorFollowing,
    negIdGenerator,
    removeObjectivesFromPlannings,
    removeObjectivesFromExceptions,
    getStationPlanningObjectiveIds,
}
